import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card, Button, Input, Select, Spinner, Empty, Badge, fmtBRL } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { Lock, Unlock, RefreshCw, ChevronDown, ChevronRight, FileDown, Sliders, Plus, X, Trash2, AlertTriangle } from "lucide-react";

const monthDefault = () => new Date().toISOString().slice(0, 7);

export default function Payroll() {
  const [competencia, setCompetencia] = useState(monthDefault());
  const [unidade, setUnidade] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [expanded, setExpanded] = useState({});
  const [pontoStatus, setPontoStatus] = useState(null); // {exists, aplicado_at}

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const params = { competencia };
      if (unidade) params.unidade = unidade;
      const { data } = await api.get("/rh/payroll", { params });
      setData(data);
      // Verifica se há snapshot de ponto e se foi aplicado
      try {
        const { data: snap } = await api.get("/rh/ponto/snapshot", { params: { competencia } });
        setPontoStatus({ exists: true, aplicado_at: snap.aplicado_at || null, aplicados_count: snap.aplicados_count || 0 });
      } catch (_) { setPontoStatus({ exists: false }); }
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [competencia, unidade]);

  const generate = async () => {
    if (!window.confirm(`Gerar/atualizar folha de ${competencia}?\nFolhas fechadas não serão sobrescritas.`)) return;
    setBusy(true); setErr("");
    try {
      const payload = { competencia };
      if (unidade) payload.unidade = unidade;
      await api.post("/rh/payroll/generate", payload);
      await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const close = async () => {
    if (!window.confirm(
      `Fechar folha COMPLETA de ${competencia}?\n\n` +
      "• Gera Vale + Saldo + INSS + IRRF + FGTS de uma vez\n" +
      "• Folhas fechadas não podem mais ser editadas (só reabertas)"
    )) return;
    setBusy(true); setErr("");
    try {
      const { data: res } = await api.post(`/rh/payroll/close?competencia=${competencia}`);
      alert(`Folha fechada! ${res.n_folhas} funcionários • ${res.aps_criadas} contas a pagar geradas.`);
      await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const closeVale = async () => {
    if (!window.confirm(
      `Fechar VALE (quinzena) de ${competencia}?\n\n` +
      "• Gera APENAS o Vale (50%) dos funcionários parcelados\n" +
      "• Folha continua editável — INSS/IRRF/FGTS ficam para o fechamento final\n" +
      "• Funcionários não-parcelados NÃO recebem AP nessa fase"
    )) return;
    setBusy(true); setErr("");
    try {
      const { data: res } = await api.post(`/rh/payroll/close/vale?competencia=${competencia}`);
      alert(`Vale gerado! ${res.n_folhas} funcionários parcelados • ${res.aps_criadas} APs criadas em Contas a Pagar.`);
      await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const closeSaldo = async () => {
    if (!window.confirm(
      `Fechar SALDO (final do mês) de ${competencia}?\n\n` +
      "• Gera o Saldo (parcelados) + Salário cheio (não-parcelados)\n" +
      "• Gera INSS, IRRF e FGTS da competência\n" +
      "• Folha vira 'fechada' — não editável (só reabertura)"
    )) return;
    setBusy(true); setErr("");
    try {
      const { data: res } = await api.post(`/rh/payroll/close/saldo?competencia=${competencia}`);
      alert(`Saldo fechado! ${res.n_folhas} funcionários • ${res.aps_criadas} contas a pagar geradas.`);
      await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const reabrir = async (pid, nome) => {
    if (!window.confirm(`Reabrir folha de ${nome}?\n\n• Se nenhuma parcela foi paga: apaga todas as APs (vale + saldo + impostos).\n• Se só o VALE foi pago: mantém a AP do vale, apaga só a AP do saldo (reabertura parcial).\n• Se o SALDO já foi pago: operação bloqueada — estorne o pagamento primeiro.`)) return;
    try { await api.post(`/rh/payroll/${pid}/reabrir`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  const reabrirTodas = async () => {
    const fechadas = items.filter((i) => i.status === "fechada" || i.vale_gerado);
    if (fechadas.length === 0) return;
    if (!window.confirm(
      `Reabrir folha de ${competencia} (${fechadas.length} funcionário${fechadas.length > 1 ? "s" : ""})?\n\n` +
      "• Folhas SEM pagamento → APs (vale + saldo + INSS + IRRF + FGTS) apagadas\n" +
      "• Folhas com o VALE pago (e saldo pendente) → apenas a AP do saldo é apagada; vale preservado\n" +
      "• Folhas com o SALDO pago → operação bloqueada; estorne o pagamento antes\n" +
      "• Depois de reaberta, a folha pode ser editada novamente"
    )) return;
    setBusy(true); setErr("");
    try {
      let ok = 0, parcial = 0, fail = 0, errs = [];
      for (const f of fechadas) {
        try {
          const { data } = await api.post(`/rh/payroll/${f.id}/reabrir`);
          if (data?.reabertura === "parcial") parcial++;
          else ok++;
        }
        catch (e) { fail++; errs.push(`${f.funcionario_nome}: ${formatErr(e)}`); }
      }
      const partes = [];
      if (ok) partes.push(`Reabertas completamente: ${ok}`);
      if (parcial) partes.push(`Reabertas parcialmente (vale preservado): ${parcial}`);
      if (fail) partes.push(`Falha em ${fail}:\n${errs.join("\n")}`);
      if (partes.length) alert(partes.join("\n\n"));
      await load();
    } finally { setBusy(false); }
  };

  const baixarHolerite = async (pid, nome) => {
    try {
      const res = await api.get(`/rh/payroll/${pid}/holerite`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `holerite_${nome.replace(/[^A-Za-z0-9]+/g, "_")}_${competencia}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert(formatErr(e)); }
  };

  // Modal de rubricas avulsas (horas extras / faltas / prêmios / etc.)
  const [ajuste, setAjuste] = useState(null); // {payroll, rubricas: []}
  const openAjuste = (i) => {
    setAjuste({ payroll: i, rubricas: [...(i.rubricas_avulsas || [])] });
  };
  const saveAjuste = async () => {
    if (!ajuste) return;
    setBusy(true); setErr("");
    try {
      await api.put(`/rh/payroll/${ajuste.payroll.id}`, ajuste.rubricas);
      setAjuste(null);
      await load();
    } catch (e) { alert(formatErr(e)); }
    finally { setBusy(false); }
  };

  const items = data?.items || [];
  const t = data?.totals || {};
  const fechada = useMemo(() => items.length > 0 && items.every((i) => i.status === "fechada"), [items]);
  const parcial = useMemo(() => items.some((i) => i.status === "fechada") && items.some((i) => i.status === "aberta"), [items]);

  return (
    <Layout
      title="Folha de Pagamento"
      subtitle="Fechamento mensal automático com INSS, IRRF e FGTS"
      actions={
        <div className="flex gap-2">
          <Input type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)} data-testid="payroll-competencia" />
          <Select value={unidade} onChange={(e) => setUnidade(e.target.value)} data-testid="payroll-unidade">
            <option value="">Todas unidades</option>
            <option value="Matriz VGS">Matriz VGS</option>
            <option value="Araxá">Araxá</option>
            <option value="Ituporanga">Ituporanga</option>
          </Select>
          <Button variant="secondary" onClick={generate} disabled={busy} data-testid="payroll-generate-btn">
            {busy ? <Spinner size={16} /> : <><RefreshCw size={16} /> Gerar</>}
          </Button>
          {!fechada && items.length > 0 && (
            <>
              {!items.every((i) => i.vale_gerado) && (
                <Button variant="secondary" onClick={closeVale} disabled={busy} data-testid="payroll-close-vale-btn">
                  <Lock size={16} /> Fechar Vale
                </Button>
              )}
              <Button onClick={closeSaldo} disabled={busy} data-testid="payroll-close-saldo-btn">
                <Lock size={16} /> Fechar Saldo
              </Button>
            </>
          )}
          {items.some((i) => i.status === "fechada" || i.vale_gerado) && (
            <Button variant="secondary" onClick={reabrirTodas} disabled={busy} data-testid="payroll-reopen-all-btn">
              <Unlock size={16} /> Reabrir folha
            </Button>
          )}
        </div>
      }
    >
      {err && <div className="text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md mb-4">{err}</div>}
      {loading ? <div className="flex justify-center py-20"><Spinner size={28} /></div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-5">
            <Kpi label="Funcionários" value={t.n || 0} />
            <Kpi label="Proventos" value={fmtBRL(t.proventos)} tone="ok" />
            <Kpi label="Descontos" value={fmtBRL(t.descontos)} tone="danger" />
            <Kpi label="Líquido CTPS" value={fmtBRL(t.liquido)} tone="ok" />
            <Kpi label="Total a pagar" value={fmtBRL(t.total_a_pagar || t.liquido)} tone="ok" strong />
            <Kpi label="INSS + IRRF" value={fmtBRL((t.inss || 0) + (t.irrf || 0))} tone="warn" />
            <Kpi label="FGTS (empresa)" value={fmtBRL(t.fgts)} tone="warn" />
          </div>

          {fechada && (
            <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-300 text-sm text-emerald-800 flex items-center gap-2">
              <Lock size={16} /> Folha de <strong>{competencia}</strong> está <strong>fechada</strong> — as contas a pagar já foram geradas em <strong>Financeiro › Contas a Pagar</strong>.
            </div>
          )}
          {pontoStatus?.exists && !pontoStatus?.aplicado_at && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-400 text-sm text-amber-900 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>Ponto importado mas ainda não foi aplicado nesta folha.</strong>
                <div className="text-xs mt-1">Vá em <strong>RH › Ponto Eletrônico</strong> e clique em <strong>&quot;Aplicar na folha&quot;</strong> — os HE, atrasos e faltas só entram no cálculo do líquido depois disso.</div>
              </div>
            </div>
          )}
          {!fechada && items.some((i) => i.vale_gerado) && (
            <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-300 text-sm text-blue-900 flex items-center gap-2">
              <Lock size={16} /> Vale já gerado para <strong>{items.filter((i) => i.vale_gerado).length}</strong> funcionário{items.filter((i) => i.vale_gerado).length > 1 ? "s" : ""} — a folha continua editável até você clicar em <strong>Fechar Saldo</strong> no final do mês.
            </div>
          )}
          {parcial && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-300 text-sm text-amber-900">
              Existem folhas abertas e fechadas nesta competência. Clique em &quot;Gerar&quot; para atualizar as abertas ou &quot;Fechar folha&quot; para consolidar as restantes.
            </div>
          )}

          {items.length === 0 ? (
            <Card><Empty title="Nenhuma folha gerada" hint={`Clique em "Gerar" para criar a folha de ${competencia} para os funcionários ativos.`} /></Card>
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full text-sm" data-testid="payroll-table">
                <thead className="bg-stone-50 border-b border-ink-200">
                  <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                    <th className="px-2 py-3 w-8"></th>
                    <th className="px-4 py-3">Funcionário</th>
                    <th className="px-4 py-3">Unidade</th>
                    <th className="px-4 py-3">Contrato</th>
                    <th className="px-4 py-3 text-right">Proventos</th>
                    <th className="px-4 py-3 text-right">INSS</th>
                    <th className="px-4 py-3 text-right">IRRF</th>
                    <th className="px-4 py-3 text-right">Líquido</th>
                    <th className="px-4 py-3 text-right">Total a pagar</th>
                    <th className="px-4 py-3 text-right">FGTS</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 w-1">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => {
                    const isOpen = expanded[i.id];
                    return (
                      <>
                      <tr key={i.id} className="border-b border-ink-200 hover:bg-stone-50 transition-colors">
                        <td className="px-2 py-3">
                          <button onClick={() => setExpanded((s) => ({ ...s, [i.id]: !s[i.id] }))} className="text-ink-500">
                            {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </td>
                        <td className="px-4 py-3 font-medium text-ink-900">
                          {i.funcionario_nome}
                          {i.comissao > 0 && <div className="text-[10px] text-warn font-semibold">+ Comissão {fmtBRL(i.comissao)}</div>}
                        </td>
                        <td className="px-4 py-3 text-ink-700 text-xs">{i.unidade}</td>
                        <td className="px-4 py-3"><Badge tone="neutral">{i.tipo_contrato}</Badge></td>
                        <td className="px-4 py-3 font-mono text-right text-emerald-700">
                          {i.tipo_contrato === "Diarista" ? (
                            <div className="flex items-center justify-end gap-2">
                              <div className="text-right">
                                <div className="text-[10px] text-ink-500 font-normal">
                                  {i.dias_trabalhados || 0}d × {fmtBRL(i.valor_diaria || 0)}
                                </div>
                                <div>{fmtBRL(i.total_proventos)}</div>
                              </div>
                              <input
                                type="number"
                                min="0"
                                max="31"
                                defaultValue={i.dias_trabalhados || 0}
                                onBlur={async (e) => {
                                  const v = Number(e.target.value) || 0;
                                  if (v === Number(i.dias_trabalhados || 0)) return;
                                  try { await api.put(`/rh/payroll/${i.id}/dias`, { dias_trabalhados: v }); await load(); }
                                  catch (err) { alert(formatErr(err)); }
                                }}
                                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                                disabled={i.status === "fechada"}
                                className="w-12 px-1 py-1 border border-ink-200 rounded text-center font-mono text-xs"
                                title="Dias trabalhados no mês"
                                data-testid={`dias-${i.id}`}
                              />
                            </div>
                          ) : (
                            fmtBRL(i.total_proventos)
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-right text-danger">
                          <div>{fmtBRL(i.inss_empregado ?? i.inss)}</div>
                          {Number(i.inss_empresa_subsidio) > 0 && (
                            <div className="text-[10px] text-emerald-700 leading-tight">
                              +{fmtBRL(i.inss_empresa_subsidio)} empresa
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-right text-danger">{fmtBRL(i.irrf)}</td>
                        <td className="px-4 py-3 font-mono text-right font-semibold">{fmtBRL(i.liquido)}</td>
                        <td className="px-4 py-3 font-mono text-right">
                          <div className="font-bold text-brand-500">{fmtBRL(i.total_a_pagar ?? i.liquido)}</div>
                          {Number(i.complemento) > 0 && (
                            <div className="text-[10px] text-ink-500 leading-tight font-normal">
                              +{fmtBRL(i.complemento)} compl
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-right text-warn text-xs">{fmtBRL(i.fgts)}</td>
                        <td className="px-4 py-3">
                          {i.status === "fechada" ? (
                            <Badge tone="success">fechada</Badge>
                          ) : i.vale_gerado ? (
                            <Badge tone="warn">vale gerado</Badge>
                          ) : (
                            <Badge tone="neutral">aberta</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => baixarHolerite(i.id, i.funcionario_nome)}
                              className="p-1.5 rounded-md hover:bg-emerald-100 text-emerald-700"
                              title="Baixar holerite (PDF)"
                              data-testid={`payroll-holerite-${i.id}`}
                            >
                              <FileDown size={16} />
                            </button>
                            {i.status === "aberta" && (
                              <button
                                onClick={() => openAjuste(i)}
                                className="p-1.5 rounded-md hover:bg-blue-100 text-blue-700"
                                title="Horas extras / Faltas / Prêmios"
                                data-testid={`payroll-adjust-${i.id}`}
                              >
                                <Sliders size={16} />
                              </button>
                            )}
                            {(i.status === "fechada" || i.vale_gerado) && (
                              <button onClick={() => reabrir(i.id, i.funcionario_nome)} className="p-1.5 rounded-md hover:bg-amber-100 text-warn" title="Reabrir" data-testid={`payroll-reopen-${i.id}`}>
                                <Unlock size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr key={`${i.id}-detail`} className="border-b border-ink-200 bg-stone-50/50">
                          <td colSpan={12} className="px-8 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div>
                                <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">Proventos</div>
                                {(i.proventos || []).map((p, ix) => (
                                  <div key={ix} className="flex justify-between text-sm py-1 border-b border-ink-100 last:border-0">
                                    <span>{p.descricao}</span>
                                    <span className="font-mono text-emerald-700">+ {fmtBRL(p.valor)}</span>
                                  </div>
                                ))}
                              </div>
                              <div>
                                <div className="text-xs font-bold uppercase tracking-wider text-danger mb-2">Descontos</div>
                                {(i.descontos || []).length === 0 ? (
                                  <div className="text-xs text-ink-500 italic">Nenhum</div>
                                ) : (i.descontos || []).map((d, ix) => (
                                  <div key={ix} className="flex justify-between text-sm py-1 border-b border-ink-100 last:border-0">
                                    <span>{d.descricao}</span>
                                    <span className="font-mono text-danger">- {fmtBRL(d.valor)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}

      {ajuste && (
        <AjusteModal
          ajuste={ajuste}
          setAjuste={setAjuste}
          onSave={saveAjuste}
          busy={busy}
        />
      )}
    </Layout>
  );
}

function AjusteModal({ ajuste, setAjuste, onSave, busy }) {
  const p = ajuste.payroll;
  const salarioBase = Number(p.total_proventos ? p.proventos?.find((x) => x.codigo === "salario_base")?.valor : 0) || 0;
  const valorHora = salarioBase / 220; // 220h mensais CLT (referência para atrasos/faltas)
  const [company, setCompany] = useState({ valor_hora_extra_50: 15, valor_hora_extra_100: 30 });
  const [tipo, setTipo] = useState("provento");
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState("");
  const [horas, setHoras] = useState("");
  const [dias, setDias] = useState("");

  useEffect(() => {
    api.get("/company").then((r) => setCompany({
      valor_hora_extra_50: Number(r.data?.valor_hora_extra_50 ?? 15),
      valor_hora_extra_100: Number(r.data?.valor_hora_extra_100 ?? 30),
    })).catch(() => {});
  }, []);

  const addRubrica = (r) => {
    if (!r.descricao || !Number(r.valor)) return;
    setAjuste({ ...ajuste, rubricas: [...ajuste.rubricas, { tipo: r.tipo, descricao: r.descricao, valor: Number(r.valor) }] });
  };
  const removeRubrica = (idx) => {
    setAjuste({ ...ajuste, rubricas: ajuste.rubricas.filter((_, i) => i !== idx) });
  };

  const addHoraExtra = (pct) => {
    const h = Number(horas);
    if (h <= 0) { alert("Informe as horas"); return; }
    const vHora = pct === 100 ? company.valor_hora_extra_100 : company.valor_hora_extra_50;
    const val = +(h * vHora).toFixed(2);
    addRubrica({ tipo: "provento", descricao: `Hora extra ${pct}% (${h}h × R$${vHora.toFixed(2)})`, valor: val });
    setHoras("");
  };
  const addFalta = (comDSR) => {
    const d = Number(dias || 1);
    let val = +(d * (salarioBase / 30)).toFixed(2);
    if (comDSR) {
      const dsr = +(val / 6).toFixed(2); // 1 DSR a cada 6 dias úteis
      addRubrica({ tipo: "desconto", descricao: `Faltas (${d} dia${d > 1 ? "s" : ""}) + DSR`, valor: val + dsr });
    } else {
      addRubrica({ tipo: "desconto", descricao: `Faltas (${d} dia${d > 1 ? "s" : ""})`, valor: val });
    }
    setDias("");
  };
  const addCustom = () => {
    if (!descricao || !Number(valor)) { alert("Descrição e valor obrigatórios"); return; }
    addRubrica({ tipo, descricao, valor: Number(valor) });
    setDescricao(""); setValor("");
  };

  const previewProventos = +(ajuste.rubricas.filter((r) => r.tipo === "provento").reduce((a, b) => a + Number(b.valor || 0), 0)).toFixed(2);
  const previewDescontos = +(ajuste.rubricas.filter((r) => r.tipo === "desconto").reduce((a, b) => a + Number(b.valor || 0), 0)).toFixed(2);

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ajuste-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-200 sticky top-0 bg-white">
          <div>
            <h3 className="font-heading font-bold text-ink-900">Ajustes na folha</h3>
            <p className="text-xs text-ink-500">{p.funcionario_nome} • {p.competencia}</p>
          </div>
          <button onClick={() => setAjuste(null)} className="text-ink-500 p-1"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Info do funcionário */}
          <div className="grid grid-cols-3 gap-2 text-xs bg-stone-50 p-3 rounded-lg">
            <div><span className="text-ink-500">Salário base:</span> <span className="font-mono font-semibold">{fmtBRL(salarioBase)}</span></div>
            <div><span className="text-ink-500">Valor da hora (ref.):</span> <span className="font-mono font-semibold">{fmtBRL(valorHora)}</span></div>
            <div><span className="text-ink-500">Valor do dia:</span> <span className="font-mono font-semibold">{fmtBRL(salarioBase / 30)}</span></div>
            <div className="col-span-3 pt-2 border-t border-ink-200">
              <span className="text-ink-500">HE 50%:</span> <span className="font-mono font-semibold text-emerald-700">{fmtBRL(company.valor_hora_extra_50)}/h</span>
              <span className="mx-2 text-ink-300">|</span>
              <span className="text-ink-500">HE 100%:</span> <span className="font-mono font-semibold text-emerald-700">{fmtBRL(company.valor_hora_extra_100)}/h</span>
              <span className="text-[10px] text-ink-500 ml-2">(configurável em /company)</span>
            </div>
          </div>

          {/* Horas extras */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">+ Horas extras</div>
            <div className="flex gap-2 items-end">
              <Input label="Qtd horas" type="number" step="0.5" value={horas} onChange={(e) => setHoras(e.target.value)} data-testid="he-horas" />
              <Button type="button" onClick={() => addHoraExtra(50)} variant="secondary" data-testid="he-50">
                <Plus size={14} /> HE 50%
              </Button>
              <Button type="button" onClick={() => addHoraExtra(100)} variant="secondary" data-testid="he-100">
                <Plus size={14} /> HE 100%
              </Button>
            </div>
            <p className="text-[11px] text-ink-500 mt-1">HE 50% = dias úteis · HE 100% = domingos e feriados</p>
          </div>

          {/* Faltas */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-danger mb-2">− Faltas / Atrasos</div>
            <div className="flex gap-2 items-end">
              <Input label="Qtd dias" type="number" step="1" value={dias} onChange={(e) => setDias(e.target.value)} data-testid="falta-dias" />
              <Button type="button" onClick={() => addFalta(false)} variant="secondary" data-testid="falta-simple">
                <Plus size={14} /> Falta
              </Button>
              <Button type="button" onClick={() => addFalta(true)} variant="secondary" data-testid="falta-dsr">
                <Plus size={14} /> Falta + DSR
              </Button>
            </div>
            <p className="text-[11px] text-ink-500 mt-1">&quot;Falta + DSR&quot; desconta também o descanso semanal remunerado proporcional</p>
          </div>

          {/* Rubrica customizada */}
          <div className="p-3 rounded-lg border border-dashed border-ink-300 bg-stone-50/50">
            <div className="text-xs font-bold uppercase tracking-wider text-ink-700 mb-2">Ajuste personalizado</div>
            <div className="flex gap-2 items-end">
              <Select label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                <option value="provento">Provento (+)</option>
                <option value="desconto">Desconto (−)</option>
              </Select>
              <Input label="Descrição" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex.: Prêmio de assiduidade" data-testid="custom-desc" />
              <Input label="Valor (R$)" type="number" step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} data-testid="custom-valor" />
              <Button type="button" onClick={addCustom} data-testid="custom-add"><Plus size={14} /> Adicionar</Button>
            </div>
          </div>

          {/* Lista das rubricas */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-ink-700 mb-2">
              Rubricas do mês ({ajuste.rubricas.length})
            </div>
            {ajuste.rubricas.length === 0 ? (
              <div className="text-sm text-ink-500 italic p-3 bg-stone-50 rounded-lg">Nenhuma rubrica avulsa — adicione acima.</div>
            ) : (
              <div className="space-y-1">
                {ajuste.rubricas.map((r, ix) => (
                  <div key={ix} className={`flex items-center justify-between px-3 py-2 rounded-md ${r.tipo === "provento" ? "bg-emerald-50" : "bg-red-50"}`}>
                    <div className="text-sm">
                      <span className={r.tipo === "provento" ? "text-emerald-700 font-semibold" : "text-danger font-semibold"}>
                        {r.tipo === "provento" ? "+" : "−"}
                      </span>{" "}
                      {r.descricao}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`font-mono font-semibold ${r.tipo === "provento" ? "text-emerald-700" : "text-danger"}`}>
                        {fmtBRL(r.valor)}
                      </span>
                      <button onClick={() => removeRubrica(ix)} className="p-1 rounded hover:bg-red-100 text-danger" data-testid={`rubrica-remove-${ix}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Prévia dos totais */}
          {(previewProventos > 0 || previewDescontos > 0) && (
            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-ink-200">
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                <div className="text-[10px] uppercase font-bold text-emerald-800">Total proventos adicionais</div>
                <div className="font-mono font-bold text-emerald-800">+ {fmtBRL(previewProventos)}</div>
              </div>
              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                <div className="text-[10px] uppercase font-bold text-red-800">Total descontos adicionais</div>
                <div className="font-mono font-bold text-red-800">− {fmtBRL(previewDescontos)}</div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-ink-200 sticky bottom-0 bg-white">
          <Button type="button" variant="secondary" onClick={() => setAjuste(null)}>Cancelar</Button>
          <Button type="button" onClick={onSave} disabled={busy} data-testid="ajuste-save">
            {busy ? <Spinner size={16} /> : "Salvar ajustes"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = "default", strong = false }) {
  const tones = { default: "text-ink-900", ok: "text-ok", danger: "text-danger", warn: "text-warn" };
  return (
    <div className={`p-4 rounded-lg border ${strong ? "bg-brand-500 border-brand-500" : "bg-white border-ink-200"}`}>
      <div className={`text-[10px] font-bold uppercase tracking-[0.1em] ${strong ? "text-emerald-100" : "text-ink-500"}`}>{label}</div>
      <div className={`font-mono text-lg font-semibold mt-1 ${strong ? "text-white" : tones[tone]}`}>{value}</div>
    </div>
  );
}
